import { useNavigation, useRouter } from 'expo-router';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { FlatList, RefreshControl, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';

import BarChart from '@/components/BarChart';
import EngagementItem from '@/components/EngagementItem';
import LeafletingAssignmentItem from '@/components/LeafletingAssignmentItem';
import PollingStationItem from '@/components/PollingStationItem';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import { getPartyColor } from '@/constants/Colors';
import AppStyles from '@/constants/Styles';
import { useBackgroundMyActiveEngagements, useBackgroundMyActiveLeafletingRoutes, useBackgroundVIStats } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { LeafletingRouteAssignment } from '@/types/leafleting';

export default function CampaignHomePage() {
  const {getSelectedBranch, getSelectedCampaign, getElectionForCampaign} = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const selectedCampaign = getSelectedCampaign();
  const selectedElection = selectedCampaign ? getElectionForCampaign(selectedCampaign, selectedBranch?.branch_short_name || '') : null;
  // Extend type to include access_expires
  const branchWithExpiry = selectedBranch as (typeof selectedBranch & { access_expires?: string | null });
  const router = useRouter();
  const navigation = useNavigation();

  // Track campaign ID to detect changes and ensure consistent hook calls
  const campaignId = selectedCampaign?.boundary_id || '';

  // Get tintColor for header icon
  const headerTintColor = useThemeColor({}, 'tint');

  // Set header title and settings icon dynamically
  useLayoutEffect(() => {
    navigation.setOptions({
      title: selectedCampaign?.boundary_name ? `${selectedCampaign.boundary_name} Election` : 'Loading...',
      headerRight: selectedBranch && selectedCampaign ? () => (
        <TouchableOpacity
          onPress={() => router.push({
            pathname: '/(main)/[branch]/campaign/[campaignId]/settings',
            params: {
              branch: selectedBranch.branch_short_name,
              campaignId: selectedCampaign.boundary_id,
            },
          })}
          style={{ padding: 8, marginRight: 12 }}
        >
          <IconSymbol name="gearshape" size={24} color={headerTintColor} />
        </TouchableOpacity>
      ) : undefined,
    });
  }, [navigation, selectedCampaign?.boundary_name, selectedBranch, selectedCampaign, router, headerTintColor]);
  
  // Use background fetch hook for my active engagements
  const { data: myEngagements, loading: engagementsLoading, error: engagementsError, refetch: refetchEngagements } = useBackgroundMyActiveEngagements();
  
  // Use background fetch hook for my active leafleting assignments
  const { data: myLeafletingAssignments, loading: leafletingLoading, error: leafletingError, refetch: refetchLeafleting } = useBackgroundMyActiveLeafletingRoutes();
  
  // Check if user is a branch officer (Commander or Lieutenant)
  const isBranchOfficer = ['commander', 'lieutenant', 'sgt major'].includes((selectedBranch?.branch_role || '').toLowerCase());
  
  // Use background fetch hook for VI stats (only for branch officers)
  const { data: viStats, loading: viStatsLoading, error: viStatsError, refetch: refetchVIStats } = useBackgroundVIStats();
  
  // Get store methods for polling stations
  const { getPollingStations, fetchPollingStations } = useCampaignStore();
  
  // Refresh state for pull-to-refresh
  const [refreshing, setRefreshing] = useState(false);
  
  // Theme-aware colors from Colors.ts
  const backgroundColor = useThemeColor({}, 'background');
  const cardBackgroundColor = useThemeColor({}, 'cardBackground');
  const borderColor = useThemeColor({}, 'border');
  const accessExpiryColor = useThemeColor({}, 'warningText');
  const labelTextColor = useThemeColor({}, 'labelText');
  const secondaryTextColor = useThemeColor({}, 'secondaryText');
  const tertiaryTextColor = useThemeColor({}, 'tertiaryText');
  const textColor = useThemeColor({}, 'text');
  const tintColor = useThemeColor({}, 'tint');

  // Refresh handlers for pull-to-refresh
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      // Refresh all data sources
      await Promise.all([
        refetchEngagements(),
        refetchLeafleting(),
        refetchVIStats(),
      ]);
    } catch (error) {
      console.warn('Failed to refresh data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const calculateDaysRemaining = (targetDate: string | null) => {
    if (!targetDate) return 0;
    const target = new Date(targetDate);
    const today = new Date();
    const diffTime = target.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Separate engagements by type
  const canvassingEngagements = useMemo(() => {
    const filtered = myEngagements?.filter(eng => eng.battle_plan_type === 'canvassing' || eng.battle_plan_type === 'gotv-door-knocking') || [];
    // Sort by number_within_battle_plan
    return filtered.sort((a, b) => {
      const nameA = a.number_within_battle_plan;
      const nameB = b.number_within_battle_plan;
      return nameA > nameB ? 1 : -1;
    });
  }, [myEngagements]);

  // Separate phone engagements by type
  const phoneEngagements = useMemo(() => {
    const filtered = myEngagements?.filter(eng => eng.battle_plan_type === 'phonebanking' || eng.battle_plan_type === 'gotv-phoning') || [];
    // Sort by number_within_battle_plan
    return filtered.sort((a, b) => {
      const nameA = a.number_within_battle_plan;
      const nameB = b.number_within_battle_plan;
      return nameA > nameB ? 1 : -1;
    });
  }, [myEngagements]);

  // Get telling polling stations that the user is part of
  const tellingPollingStations = useMemo(() => {
    const filtered = myEngagements?.filter(eng => eng.battle_plan_type === 'gotv-telling') || [];
    const battlePlanMap = new Map<string, string>();
    
    filtered.forEach(eng => {
      battlePlanMap.set(eng.battle_plan_id, eng.battle_plan_name);
    });
    
    // Get all polling stations for battle plans the user is part of
    const userPollingStations: { pollingStation: any; battlePlanId: string; battlePlanName: string }[] = [];
    
    battlePlanMap.forEach((battlePlanName, battlePlanId) => {
      const pollingStations = getPollingStations(battlePlanId);
      if (pollingStations && pollingStations.length > 0) {
        // Filter polling stations to only include those with engagements the user is part of
        const userEngagementIds = filtered
          .filter(eng => eng.battle_plan_id === battlePlanId)
          .map(eng => eng.engagement_id);
        
        const relevantPollingStations = pollingStations.filter(ps => 
          ps.polling_station_districts?.some(district => 
            userEngagementIds.includes(district.engagement_id)
          )
        );
        
        relevantPollingStations.forEach(pollingStation => {
          userPollingStations.push({
            pollingStation,
            battlePlanId,
            battlePlanName
          });
        });
      }
    });
    
    return userPollingStations;
  }, [myEngagements, getPollingStations]);

  // Fetch polling stations for each battle plan if not in store
  React.useEffect(() => {
    const filtered = myEngagements?.filter(eng => eng.battle_plan_type === 'gotv-telling') || [];
    const battlePlanIds = [...new Set(filtered.map(eng => eng.battle_plan_id))];
    
    battlePlanIds.forEach(battlePlanId => {
      const pollingStations = getPollingStations(battlePlanId);
      if (!pollingStations || pollingStations.length === 0) {
        fetchPollingStations(battlePlanId).catch((err) => {
          console.warn('Error fetching polling stations:', err);
        });
      }
    });
  }, [myEngagements, getPollingStations, fetchPollingStations]);

  // Calculate days remaining (handle null election gracefully)
  const electionDaysRemaining = selectedElection ? calculateDaysRemaining(selectedElection.polling_day) : 0;
  const postalDaysRemaining = selectedElection ? calculateDaysRemaining(selectedElection.postal_votes_issued) : 0;

  // VI label mapping
  const viLabels: { [key: string]: string } = {
    'R': 'Reform',
    'Z': 'Possible Reform',
    'A': 'Against Reform',
    'C': 'Conservative',
    'G': 'Green',
    'I': 'Independent',
    'L': 'Liberal Democrat',
    'N': 'Nationalist',
    'S': 'Labour',
    'U': 'Undecided',
    'W': 'Will not vote',
  };

  // Prepare bar chart data from VI stats in specified order: R, Z | C, G, I, L, N, S | A | U, W
  const viChartData = useMemo(() => {
    if (!viStats?.vi_counts) return [];
    
    // Define the order and grouping
    const viOrder: string[] = ['R', 'Z', 'C', 'G', 'I', 'L', 'N', 'S', 'A', 'U', 'W'];
    // Groups: R, Z (end at Z) | C, G, I, L, N, S (end at S) | A (end at A) | U, W (end at W)
    const separatorAfter: string[] = ['Z', 'S', 'A', 'W'];
    
    // Create a map of existing VI codes
    const viMap = new Map<string, number>();
    Object.entries(viStats.vi_counts).forEach(([code, count]) => {
      viMap.set(code.toUpperCase(), typeof count === 'number' ? count : 0);
    });
    
    // Build chart data in specified order, only including codes that exist in the data
    return viOrder
      .filter(code => viMap.has(code))
      .map((code, index, filteredOrder) => {
        const count = viMap.get(code) || 0;
        const showSeparator = separatorAfter.includes(code) && index < filteredOrder.length - 1;
        
        return {
          label: viLabels[code] || code,
          shortLabel: code,
          value: count,
          color: getPartyColor(code) || '#808080',
          showSeparatorAfter: showSeparator,
        };
      });
  }, [viStats]);

  // Show loading state if election data is not available yet
  if (!selectedElection) {
    return (
      <ThemedView style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
        <ThemedText>Loading campaign data...</ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={{flex: 1}} key={campaignId}>
      <ScrollView 
        contentContainerStyle={[styles.container, { backgroundColor }]}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={tintColor}
            colors={[tintColor]}
          />
        }
      >
        {/* Access Expiry Line */}
        {branchWithExpiry?.access_expires && (
          <ThemedText style={{ textAlign: 'right', color: accessExpiryColor, marginBottom: 12 }}>
            {(() => {
              const d = new Date(branchWithExpiry.access_expires!);
              const day = d.getDate();
              const month = d.toLocaleString('en-GB', { month: 'short' });
              const hour = d.getHours().toString().padStart(2, '0');
              const min = d.getMinutes().toString().padStart(2, '0');
              return `Access expires: ${day} ${month}, ${hour}:${min}`;
            })()}
          </ThemedText>
        )}

        {/* Countdown Section */}
        <View style={styles.countdownSection}>
          <View style={[styles.countdownCard, { backgroundColor: cardBackgroundColor, borderColor }]}>
            <ThemedText type="subtitle" style={[styles.countdownLabel, { color: labelTextColor }]}>
              Election Day
            </ThemedText>
            <ThemedText style={styles.countdownNumber}>
              {electionDaysRemaining}
            </ThemedText>
            <ThemedText style={[styles.countdownText, { color: secondaryTextColor }]}>
              {electionDaysRemaining === 1 ? 'day' : 'days'} remaining
            </ThemedText>
            <ThemedText style={[styles.dateText, { color: tertiaryTextColor }]}>
              {new Date(selectedElection.polling_day).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short' 
              })}
            </ThemedText>
          </View>

          <View style={[styles.countdownCard, { backgroundColor: cardBackgroundColor, borderColor }]}>
            <ThemedText type="subtitle" style={[styles.countdownLabel, { color: labelTextColor }]}>
              Postal Votes
            </ThemedText>
            <ThemedText style={styles.countdownNumber}>
              {postalDaysRemaining}
            </ThemedText>
            <ThemedText style={[styles.countdownText, { color: secondaryTextColor }]}>
              {postalDaysRemaining === 1 ? 'day' : 'days'} until delivery
            </ThemedText>
            <ThemedText style={[styles.dateText, { color: tertiaryTextColor }]}>
              {selectedElection.postal_votes_issued 
                ? new Date(selectedElection.postal_votes_issued).toLocaleDateString('en-GB', { 
                    day: 'numeric', 
                    month: 'short' 
                  })
                : 'TBD'}
            </ThemedText>
          </View>
        </View>

        {/* Canvassing Section - Only show if user has canvassing engagements */}
        {!engagementsLoading && !engagementsError && canvassingEngagements.length > 0 && (
          <View style={AppStyles.cardSection}>
            <ThemedText type="subtitle" style={AppStyles.subtitleText}>
              Canvassing
            </ThemedText>
            
            <FlatList
              data={canvassingEngagements}
              keyExtractor={(item) => item.engagement_id}
              renderItem={({ item }) => (
                <EngagementItem 
                  engagement={item}
                  onPress={(engagement) => {
                    // Navigate to canvassing engagement details screen
                    if (selectedBranch?.branch_short_name && selectedCampaign?.boundary_id) {
                      router.push({
                        pathname: '/(main)/[branch]/campaign/[campaignId]/canvass/[battlePlanId]/[engagementId]',
                        params: { 
                          branch: selectedBranch.branch_short_name,
                          campaignId: selectedCampaign.boundary_id,
                          battlePlanId: engagement.battle_plan_id,
                          engagementId: engagement.engagement_id
                        }
                      });
                    }
                  }}
                  showProgressBars={true}
                  showStats={true}
                />
              )}
              scrollEnabled={false}
              showsVerticalScrollIndicator={false}
            />
          </View>
        )}

        {/* Phone Section - Only show if user has phone engagements */}
        {!engagementsLoading && !engagementsError && phoneEngagements.length > 0 && (
          <View style={AppStyles.cardSection}>
            <ThemedText type="subtitle" style={AppStyles.subtitleText}>
              Phone Campaigning
            </ThemedText>
            
            <FlatList
              data={phoneEngagements}
              keyExtractor={(item) => item.engagement_id}
              renderItem={({ item }) => (
                <EngagementItem 
                  engagement={item}
                  onPress={(engagement) => {
                    // Navigate to phone engagement details screen
                    if (selectedBranch?.branch_short_name && selectedCampaign?.boundary_id) {
                      router.push({
                        pathname: '/(main)/[branch]/campaign/[campaignId]/phone/[battlePlanId]/[engagementId]',
                        params: { 
                          branch: selectedBranch.branch_short_name,
                          campaignId: selectedCampaign.boundary_id,
                          battlePlanId: engagement.battle_plan_id,
                          engagementId: engagement.engagement_id
                        }
                      });
                    }
                  }}
                  showProgressBars={true}
                  showStats={true}
                />
              )}
              scrollEnabled={false}
              showsVerticalScrollIndicator={false}
            />
          </View>
        )}

        {/* Leafleting Section - Only show if user has assignments */}
        {!leafletingLoading && !leafletingError && myLeafletingAssignments && myLeafletingAssignments.length > 0 && (
          <View style={AppStyles.cardSection}>
            <ThemedText type="subtitle" style={AppStyles.subtitleText}>
              Leafleting
            </ThemedText>
            
            <FlatList
              data={myLeafletingAssignments}
              keyExtractor={(item) => item.assignment_id}
              renderItem={({ item }) => (
                <LeafletingAssignmentItem 
                  assignment={item}
                  onPress={(assignment: LeafletingRouteAssignment) => {
                    // Navigate directly to the assignment details screen
                    if (selectedBranch?.branch_short_name && selectedCampaign?.boundary_id) {
                      router.push({
                        pathname: '/(main)/[branch]/campaign/[campaignId]/leafleting/[roundId]/[assignmentId]',
                        params: { 
                          branch: selectedBranch.branch_short_name,
                          campaignId: selectedCampaign.boundary_id,
                          roundId: assignment.round_global_id,
                          assignmentId: assignment.assignment_id
                        }
                      });
                    }
                  }}
                />
              )}
              scrollEnabled={false}
              showsVerticalScrollIndicator={false}
            />
          </View>
        )}

        {/* Telling Section - Show polling stations the user is part of */}
        {!engagementsLoading && !engagementsError && tellingPollingStations.length > 0 && (
          <View style={AppStyles.cardSection}>
            <ThemedText type="subtitle" style={AppStyles.subtitleText}>
              Telling
            </ThemedText>
            
            <FlatList
              data={tellingPollingStations}
              keyExtractor={(item) => `${item.battlePlanId}-${item.pollingStation.polling_station_id}`}
              renderItem={({ item }) => (
                <PollingStationItem 
                  pollingStation={item.pollingStation}
                  onPress={(item) => {
                    // Navigate to the first engagement of this polling statio
                    
                    if (selectedBranch?.branch_short_name && selectedCampaign?.boundary_id) {
                      router.push({
                        pathname: '/(main)/[branch]/campaign/[campaignId]/telling/[battlePlanId]/[pollingStationId]',
                        params: { 
                          branch: selectedBranch.branch_short_name,
                          campaignId: selectedCampaign.boundary_id,
                          battlePlanId: item.battle_plan_id,
                          pollingStationId: item.polling_station_id.toString()
                        }
                      });
                    }
                  }}
                />
              )}
              scrollEnabled={false}
              showsVerticalScrollIndicator={false}
            />
          </View>
        )}

        {/* VI Stats Section - Only show for branch officers */}
        {isBranchOfficer && (
          <View style={AppStyles.cardSection}>
            <ThemedText type="subtitle" style={AppStyles.subtitleText}>
              VI Stats
            </ThemedText>
            
            {viStatsLoading && !viStats && (
              <ThemedText style={{ textAlign: 'center', color: secondaryTextColor, padding: 20 }}>
                Loading VI stats...
              </ThemedText>
            )}
            
            {viStatsError && !viStats && (
              <ThemedText style={{ textAlign: 'center', color: accessExpiryColor, padding: 20 }}>
                Error loading VI stats
              </ThemedText>
            )}
            
            {viStats && (
              <>
                {/* Totals */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                  <ThemedText style={{ color: textColor, flex: 1, minWidth: 100 }}>
                    Addresses: {viStats.total_addresses > 0 ? Math.round((viStats.total_households_with_vis / viStats.total_addresses) * 100) : 0}%
                  </ThemedText>
                  <ThemedText style={{ color: textColor, flex: 1, minWidth: 100 }}>
                    Voters: {viStats.total_voters > 0 ? Math.round((viStats.total_voters_with_vis / viStats.total_voters) * 100) : 0}%
                  </ThemedText>
                  <ThemedText style={{ color: textColor, flex: 1, minWidth: 100 }}>
                    Phone Numbers: {viStats.total_phone_numbers}
                  </ThemedText>
                </View>
                
                {/* Bar Chart */}
                {viChartData.length > 0 && (
                  <BarChart data={viChartData} height={300} showValues={true} />
                )}
                
                {viChartData.length === 0 && (
                  <ThemedText style={{ textAlign: 'center', color: secondaryTextColor, padding: 20 }}>
                    No VI data available
                  </ThemedText>
                )}
              </>
            )}
          </View>
        )}
      </ScrollView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
  },
  countdownSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 40,
  },
  countdownCard: {
    flex: 1,
    marginHorizontal: 5,
    alignItems: 'center',
    ...AppStyles.card,
    borderWidth: 1,
  },
  countdownLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  countdownNumber: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#e74c3c',
    marginBottom: 4,
    lineHeight: 40,
    textAlignVertical: 'center',
  },
  countdownText: {
    fontSize: 14,
    marginBottom: 8,
  },
  dateText: {
    fontSize: 12,
    fontWeight: '500',
  },
}); 